import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from './cn';

export type CardVariant = 'default' | 'glass';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
}

const variantClasses: Record<CardVariant, string> = {
  default: 'bg-bg border border-border rounded-lg shadow-sm',
  glass:
    'bg-surface border border-border rounded-[var(--r-lg,1.5rem)] shadow-[var(--shadow-1,0_1px_2px_rgba(0,0,0,0.04))] backdrop-blur-md',
};

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3.5',
  md: 'p-5',
  lg: 'p-6',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', padding = 'none', className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('text-foreground', variantClasses[variant], paddingClasses[padding], className)}
      {...rest}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-1.5 border-b border-border p-4', className)}
        {...rest}
      />
    );
  },
);

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardBody({ className, ...rest }, ref) {
    return <div ref={ref} className={cn('p-4', className)} {...rest} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-end gap-2 border-t border-border p-4',
          className,
        )}
        {...rest}
      />
    );
  },
);
